import { useState } from 'react';
import {
    Screen, Section, SectionHeader, Divider, Bleed,
    Button, ButtonFooter,
    Badge, TimeBadge, ChipOption,
    Avatar, AvatarStack, AttendanceLegend, Person,
    PhotoGrid, CardRail, PlaceCard, ListCard, MeetingCard, EmptyState,
    InverseBanner, InverseTime,
    TopBar, Tabs, FilterBar, SearchInput, BottomGnb, Sheet,
} from 'components/ds';
import styles from './Showcase.module.css';

/** 클래스 이름을 해시된 이름으로 바꿉니다. 모르는 이름은 그대로 둡니다.
 *  이름을 바꾸지 않고 스코프만 씌우려고 둔 얇은 헬퍼입니다. */
const cls = (names?: string): string =>
    (names || '').split(' ').filter(Boolean)
        .map((n) => (styles as Record<string, string>)[n] || n).join(' ');

/**
 * 디자인 시스템 쇼케이스 — /ds
 *
 * 개발 확인용 화면입니다. 실제 서비스 라우트가 아니며 GNB/딥링크와 무관합니다.
 * 여기 문구는 데모용이라 i18n 을 타지 않습니다 (실제 화면에서는 t() 로 넘깁니다).
 */

const PEOPLE: Person[] = [
    { userNo: 1, name: '재권', attendance: 'going', isHost: true },
    { userNo: 2, name: '민서', attendance: 'going' },
    { userNo: 3, name: '지훈', attendance: 'pending' },
    { userNo: 4, name: '수아', attendance: 'declined' },
    { userNo: 5, name: '태윤', attendance: 'going' },
    { userNo: 6, name: '하늘', attendance: 'pending' },
];

const PHOTOS = Array.from({ length: 8 }, (_, i) => ({
    id: i,
    url: '',
    alt: '',
}));

const CATS: ChipOption[] = [
    { value: 'all', label: '전체' },
    { value: 'food', label: '맛집' },
    { value: 'cafe', label: '카페' },
    { value: 'stay', label: '숙소' },
    { value: 'spot', label: '관광지' },
];

const IcHome = (on: boolean) => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth={on ? 2.4 : 1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    </svg>
);
const IcList = (on: boolean) => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth={on ? 2.4 : 1.8} strokeLinecap="round" aria-hidden>
        <line x1="4" y1="7" x2="20" y2="7" /><line x1="4" y1="12" x2="20" y2="12" />
        <line x1="4" y1="17" x2="14" y2="17" />
    </svg>
);
const IcChat = (on: boolean) => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth={on ? 2.4 : 1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
);
const IcMy = (on: boolean) => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth={on ? 2.4 : 1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <circle cx="12" cy="8" r="4" /><path d="M4 21c0-4 3.6-6 8-6s8 2 8 6" />
    </svg>
);
const IcEmpty = () => (
    <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <rect x="3" y="5" width="18" height="16" rx="2" />
        <line x1="3" y1="10" x2="21" y2="10" />
        <line x1="8" y1="3" x2="8" y2="7" /><line x1="16" y1="3" x2="16" y2="7" />
    </svg>
);

export const Showcase = () => {
    const [tab, setTab] = useState('upcoming');
    const [cat, setCat] = useState('all');
    const [q, setQ] = useState('');
    const [gnb, setGnb] = useState('home');
    const [sheet, setSheet] = useState(false);

    return (
        <Screen hasGnb hasFooter top={<TopBar title="디자인 시스템" onBack={() => window.history.back()} backLabel="뒤로" />}>
            <Section spacing="tight">
                <Tabs
                    ariaLabel="모임 상태"
                    value={tab}
                    onChange={setTab}
                    items={[
                        { value: 'upcoming', label: '예정된 모임', count: 3 },
                        { value: 'past', label: '지난 모임', count: 24 },
                    ]}
                />
            </Section>

            {/* 반전 배너 — copyTone 문구 자리 */}
            <Section spacing="tight">
                <Bleed>
                    <InverseBanner
                        title="불금이에요, 재권님!"
                        sub={<>가장 가까운 약속은 <InverseTime>2시간 후</InverseTime> · 합정 소금집</>}
                        onClick={() => undefined}
                    />
                </Bleed>
            </Section>

            {/* 모임 카드 */}
            <Section>
                <SectionHeader
                    title="예정된 모임"
                    count={3}
                    sub="이번 주에 만나기로 한 약속이에요"
                    moreLabel="전체보기"
                    onMore={() => undefined}
                />
                <div className={cls("ds-stack")}>
                    <MeetingCard
                        name="금요일 저녁 번개"
                        when="9월 5일 (금) 오후 7시"
                        where="서울 마포구 양화로 178-5"
                        time={<TimeBadge urgent>D-3</TimeBadge>}
                        badges={[{ label: '정산 대기', tone: 'danger' }, { label: '주최자', tone: 'weak' }]}
                        people={<AvatarStack people={PEOPLE} size="sm" labels={{ going: '참여', pending: '미정', declined: '불참' }} />}
                        onClick={() => undefined}
                    />
                    <MeetingCard
                        name="제주 3박 4일"
                        when="9월 20일 (토) ~ 9월 23일 (화)"
                        where="제주특별자치도 제주시"
                        time={<TimeBadge>D-19</TimeBadge>}
                        badges={[{ label: '여행', tone: 'lime' }]}
                        people={<AvatarStack people={PEOPLE.slice(0, 3)} size="sm" />}
                        onClick={() => undefined}
                    />
                </div>
            </Section>

            <Divider thick />

            {/* 가로 카드 레일 */}
            <Section>
                <SectionHeader
                    title="추천 장소"
                    count={12}
                    moreLabel="전체보기"
                    onMore={() => undefined}
                />
                <CardRail>
                    <PlaceCard name="카페 미아" meta="연남동" badge={{ label: 'NEW' }} onClick={() => undefined} />
                    <PlaceCard name="소금집" meta="망원동" onClick={() => undefined} />
                    <PlaceCard name="테라스 22" meta="합정" onClick={() => undefined} />
                    <PlaceCard name="더 플레이스" meta="홍대입구" onClick={() => undefined} />
                </CardRail>
            </Section>

            {/* 사진 그리드 — 장소 상세의 사진들 (PhotoLightbox 로 열리는 그 사진).
                모임 사진 업로드 기능은 아직 없습니다 (MeetingPostsSection 은 텍스트 전용). */}
            <Section>
                <SectionHeader title="장소 사진" count={12} moreLabel="전체보기" onMore={() => undefined} />
                <PhotoGrid photos={PHOTOS} max={6} />
            </Section>

            <Divider thick />

            {/* 검색 + 필터 + 리스트 */}
            <Section>
                <SectionHeader title="장소 찾기" gap={false} />
                <div className={cls("ds-gap")} />
                <SearchInput value={q} onChange={setQ} placeholder="장소명을 입력해주세요" clearLabel="지우기" />
                <FilterBar
                    ariaLabel="카테고리"
                    options={CATS}
                    value={cat}
                    onChange={setCat}
                    sortLabel="가까운 순"
                    onSort={() => setSheet(true)}
                />
                <ListCard
                    title="일상의틈 홍대입구점"
                    sub="서울특별시 마포구 양화로 178-5"
                    tags={['카페', '주차 가능']}
                    aside="130m"
                    onClick={() => undefined}
                />
                <ListCard
                    title="소금집 델리"
                    sub="서울특별시 마포구 월드컵로 12"
                    tags={['맛집']}
                    aside="420m"
                    onClick={() => undefined}
                />
            </Section>

            <Divider thick />

            {/* 참여자 — 상태를 형태로 */}
            <Section>
                <SectionHeader
                    title="참여자"
                    count={6}
                    sub="참여 상태는 색이 아니라 테두리 모양으로 구분합니다"
                />
                <div className={cls("ds-avatars")}>
                    {PEOPLE.map((p) => (
                        <Avatar key={p.userNo} person={p} size="lg" />
                    ))}
                </div>
                <AttendanceLegend labels={{ going: '참여', pending: '미정', declined: '불참' }} />
            </Section>

            {/* 배지 · 시간 */}
            <Section>
                <SectionHeader title="배지와 시간" sub="브랜드색은 시간에만 씁니다" />
                <div className={cls("ds-row")}>
                    <TimeBadge size="lg" urgent>지금 출발</TimeBadge>
                    <TimeBadge size="md">2시간 후</TimeBadge>
                    <TimeBadge size="sm">D-3</TimeBadge>
                    <TimeBadge size="sm" past>지난 모임</TimeBadge>
                </div>
                <div className={cls("ds-row")}>
                    <Badge tone="brand">확정</Badge>
                    <Badge tone="weak">주최자</Badge>
                    <Badge tone="lime">NEW</Badge>
                    <Badge tone="neutral">6명 참여</Badge>
                    <Badge tone="danger">정산 필요</Badge>
                    <Badge tone="outline">여행</Badge>
                </div>
            </Section>

            {/* 버튼 */}
            <Section>
                <SectionHeader title="버튼" sub="주 동작은 브랜드색이 아니라 검정입니다" />
                <div className={cls("ds-row")}>
                    <Button variant="primary">모임 만들기</Button>
                    <Button variant="brand">출발 알리기</Button>
                    <Button variant="secondary">초대 링크</Button>
                    <Button variant="ghost">나중에</Button>
                    <Button variant="danger">모임 나가기</Button>
                </div>
                <div className={cls("ds-row")}>
                    <Button size="sm" variant="secondary">작은 버튼</Button>
                    <Button size="sm" variant="ghost" disabled>비활성</Button>
                </div>
                <div className={cls("ds-gap")} />
                <Button variant="primary" size="lg" block onClick={() => setSheet(true)}>
                    바텀시트 열기
                </Button>
            </Section>

            {/* 빈 상태 */}
            <Section>
                <SectionHeader title="빈 상태" />
                <EmptyState
                    icon={<IcEmpty />}
                    title="아직 모임이 없어요"
                    body="친구를 초대하거나 초대 코드를 입력하면 여기에 모임이 쌓입니다."
                    action={<Button variant="primary">첫 모임 만들기</Button>}
                />
            </Section>

            <Sheet open={sheet} onClose={() => setSheet(false)} title="정렬 기준" closeLabel="닫기">
                <div className={cls("ds-sheet-list")}>
                    {['가까운 순', '인기 순', '최신 순', '이름 순'].map((s) => (
                        <button key={s} type="button" className={cls("ds-sheet-item")} onClick={() => setSheet(false)}>
                            {s}
                        </button>
                    ))}
                </div>
            </Sheet>

            <ButtonFooter aboveGnb>
                <Button variant="secondary" onClick={() => undefined}>공유</Button>
                <Button variant="primary" block onClick={() => undefined}>참여하기</Button>
            </ButtonFooter>

            <BottomGnb
                value={gnb}
                onChange={setGnb}
                items={[
                    { value: 'home', label: '홈', icon: IcHome },
                    { value: 'meetings', label: '모임', icon: IcList, dot: true },
                    { value: 'chat', label: '채팅', icon: IcChat },
                    { value: 'my', label: '마이', icon: IcMy },
                ]}
            />
        </Screen>
    );
};
